import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    useContainerPosition,
    useScroller,
    useResizeObserver,
    useMasonry
} from 'masonic';
import { useWindowSize } from '@react-hook/window-size';
import { usePositioner } from 'src/utils/usePositioner';
import { useScrollToIndex } from 'src/utils/useScrollToIndex';

const MasonryLayout = ({ items, gap, scrollToIndex, render, columnWidth }) => {
    const [lastScrollTop, setLastScrollTop] = useState(0);
    const [lastIsScrolling, setLastIsScrolling] = useState(false);
    const containerRef = React.useRef(null);
    const [windowWidth, windowHeight] = useWindowSize();
    const { offset, width } = useContainerPosition(containerRef, [
        windowWidth,
        windowHeight
    ]);

    const { scrollTop, isScrolling } = useScroller(offset);

    useEffect(() => {
        if (document.body.style.position === 'fixed' || scrollTop === lastScrollTop) {
            setLastIsScrolling(false);
            return;
        }

        setLastScrollTop(scrollTop);
        setLastIsScrolling(isScrolling);
    }, [scrollTop, isScrolling]);

    const positioner = usePositioner({
        width,
        columnWidth,
        columnGutter: gap,
        cellItems: items
    });

    const scrollToIndexWithPositioner = useScrollToIndex(positioner, { offset: 240 });

    const resizeObserver = useResizeObserver(positioner);

    useEffect(() => {
        if (scrollToIndex != null) {
            scrollToIndexWithPositioner(scrollToIndex);
        }
    }, [scrollToIndex]);

    return useMasonry({
        scrollTop: lastScrollTop,
        positioner,
        isScrolling: lastIsScrolling,
        resizeObserver,
        items,
        containerRef,
        render,
        height: windowHeight
    });
};

MasonryLayout.propTypes = {
    columnWidth: PropTypes.number,
    gap: PropTypes.number,
    items: PropTypes.arrayOf(PropTypes.object),
    scrollToIndex: PropTypes.number,
    render: PropTypes.func.isRequired
};
MasonryLayout.defaultProps = {
    columnWidth: 320,
    gap: 20
};

export default MasonryLayout;
